import { InputMaybe, PreferredHotelProviderType, PriceComparisonPayload, PriceComparisonType, DeviceType } from '@graphql/generated';
import { useRemoteFeatureFlag } from '@shared/core/featureFlags/remoteFeatureFlags';
import { useMemo } from 'react';
import { useApiMetadata } from './useApiMetadata';
import { useClientReference } from './useClientReference';
import { PricingApiCallSource } from '@apps/stays/types';
import { usePricingCallSource } from './usePricingCallSource';

const isValidPriceComparisonType = (value?: InputMaybe<PriceComparisonType>): value is PriceComparisonType =>
  Object.values(PriceComparisonType).includes(value as PriceComparisonType);

const isValidPreferredHotelProviderType = (value?: InputMaybe<PreferredHotelProviderType>): value is PreferredHotelProviderType =>
  Object.values(PreferredHotelProviderType).includes(value as PreferredHotelProviderType);

const isValidMarginPercentage = (value?: InputMaybe<number>): value is number => typeof value === 'number' && !isNaN(value) && value >= 0 && value <= 100;

const isValidPriceComparePayload = (payload: PriceComparisonPayload | undefined) => {
  if (!payload) return false;
  return (
    isValidPriceComparisonType(payload.comparisonType) &&
    isValidPreferredHotelProviderType(payload.preferredProvider) &&
    isValidMarginPercentage(payload.preferredProviderPriceMaxExtraPercent)
  );
};

export type BaseSearchMetadata = Readonly<{
  eventId: string | undefined;
  anonymousId: string | undefined;
  deviceType: DeviceType | undefined;
  userCountryCode: string | undefined;
  preferredProviderType: PreferredHotelProviderType | undefined;
  priceComparisonPayload: PriceComparisonPayload | undefined;
}>;

export type SearchMetadataWithCallSource = Readonly<
  BaseSearchMetadata & {
    callSource: PricingApiCallSource | undefined;
  }
>;

export const useBaseSearchMetadata = () => {
  const { deviceType, anonymousId, eventId, userCountryCode } = useApiMetadata();
  const { loadingClientReference } = useClientReference();

  const {
    value: accommodationsGuestPriceCompareType,
    loading: accommodationsGuestPriceCompareTypeLoading,
    payload: accommodationsGuestPriceCompareTypeLoadingPayload,
  } = useRemoteFeatureFlag('accommodationsGuestPriceCompareType');

  const preferredProviderType = accommodationsGuestPriceCompareType as PreferredHotelProviderType;
  const priceComparisonPayload = useMemo(() => {
    const remotePayload = (Object(accommodationsGuestPriceCompareTypeLoadingPayload) as PriceComparisonPayload) || {};
    return {
      comparisonType: remotePayload.comparisonType,
      preferredProvider: remotePayload.preferredProvider,
      preferredProviderPriceMaxExtraPercent: remotePayload.preferredProviderPriceMaxExtraPercent,
    };
  }, [accommodationsGuestPriceCompareTypeLoadingPayload]);

  const shouldUsePriceComparison = accommodationsGuestPriceCompareType === 'pricecomparison';
  let validPriceComparisonPayload: PriceComparisonPayload | undefined;
  if (shouldUsePriceComparison && isValidPriceComparePayload(priceComparisonPayload)) {
    validPriceComparisonPayload = priceComparisonPayload;
  }

  let validPreferredProviderType: PreferredHotelProviderType | undefined;
  if ((!shouldUsePriceComparison || !validPriceComparisonPayload) && isValidPreferredHotelProviderType(preferredProviderType)) {
    validPreferredProviderType = preferredProviderType;
  }

  const isPreparingMetadata = loadingClientReference || accommodationsGuestPriceCompareTypeLoading;

  const baseMetadata: BaseSearchMetadata = useMemo(
    () => ({
      eventId,
      deviceType,
      anonymousId,
      userCountryCode,
      preferredProviderType: validPreferredProviderType,
      priceComparisonPayload: validPriceComparisonPayload,
    }),
    [eventId, validPriceComparisonPayload, validPreferredProviderType, anonymousId, deviceType, userCountryCode],
  );

  return {
    baseMetadata,
    isPreparingMetadata,
  };
};

/**
 * Hook for generating metadata for queries that support callSource tracking.
 * Used by: travelPlacesPricingAndBookingDetails, travelPricingsForAccommodationByPropertyId
 */
export const useSearchAccommodationsQueryMetadataPayload = (
  callSource?: PricingApiCallSource,
): {
  isPreparingSearchMetadataPayload: boolean;
  searchMetadata: SearchMetadataWithCallSource | null;
} => {
  const { isPreparingMetadata, baseMetadata } = useBaseSearchMetadata();
  const autoDetectedCallSource = usePricingCallSource();
  const effectiveCallSource = callSource ?? autoDetectedCallSource;

  return useMemo(() => {
    return {
      isPreparingSearchMetadataPayload: isPreparingMetadata,
      searchMetadata: isPreparingMetadata
        ? null
        : {
            ...baseMetadata,
            callSource: effectiveCallSource,
          },
    };
  }, [isPreparingMetadata, baseMetadata, effectiveCallSource]);
};

/**
 * Hook for generating metadata for extended accommodation search queries.
 * Used by: searchExtendedAccommodationProperties, searchExtendedAccommodationPropertiesV2
 */
export const useExtendedAccommodationSearchMetadata = (): {
  isPreparingSearchMetadata: boolean;
  searchMetadata: BaseSearchMetadata | null;
} => {
  const { isPreparingMetadata, baseMetadata } = useBaseSearchMetadata();

  return useMemo(() => {
    return {
      isPreparingSearchMetadata: isPreparingMetadata,
      searchMetadata: isPreparingMetadata ? null : baseMetadata,
    };
  }, [isPreparingMetadata, baseMetadata]);
};
